import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  CheckCircle,
  Warning,
  XCircle,
  Info,
  ArrowsHorizontal,
  Clock,
} from '@phosphor-icons/react';

/**
 * EVV Cross-Check review modal.
 *
 * When a confirmed timesheet is ready to submit to EVV, we first fetch a
 * per-entry reconciliation (timesheet times vs existing EVV GPS times) so the
 * user sees discrepancies before any new visits are created.
 *
 * Each entry has:
 *   - exact_match → green check, auto-accept
 *   - minor_drift → amber, auto-accept (EVV times win by default)
 *   - major_drift → orange, requires user choice
 *   - no_evv_record → blue, a new EVV visit will be created from the timesheet
 *   - supervisor_required → red, submission blocked
 */
export const EVVCrossCheckModal = ({ open, onClose, timesheetId, onSubmitted }) => {
  const [loading, setLoading] = useState(true);
  const [reconciliations, setReconciliations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [perEntryChoices, setPerEntryChoices] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !timesheetId) return;
    let cancelled = false;
    setLoading(true);
    setPerEntryChoices({});
    api
      .get(`/timesheets/${timesheetId}/evv-preview`)
      .then((res) => {
        if (cancelled) return;
        setReconciliations(res.data.reconciliations || []);
        setSummary(res.data.summary || null);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Failed to load EVV cross-check');
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, timesheetId, onClose]);

  const handleChoice = (index, mode) => {
    setPerEntryChoices((prev) => ({ ...prev, [index]: mode }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/timesheets/${timesheetId}/submit-evv`, {
        timesheet_id: timesheetId,
        service_code: 'T1019',
        verification_method: 'Manual',
        per_entry_choices: perEntryChoices,
      });
      toast.success(res.data.message);
      onSubmitted && onSubmitted();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'EVV submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            data-testid="evv-cross-check-title"
          >
            <ArrowsHorizontal size={22} className="text-indigo-700" />
            EVV Cross-Check Review
          </DialogTitle>
          <DialogDescription>
            We compared your timesheet entries to existing EVV visits.{' '}
            <strong>EVV times (GPS-authoritative) will be used by default.</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <SummaryBar summary={summary} />
            <div
              className="flex-1 overflow-y-auto space-y-3 py-2"
              data-testid="evv-cross-check-list"
            >
              {reconciliations.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No entries to reconcile.
                </div>
              )}
              {reconciliations.map((r, idx) => {
                // Stable key based on entry content — survives re-render even if
                // the parent re-fetches preview (defensive; the modal is stateful
                // about per-entry choices keyed by idx, so we keep idx for that).
                const entryKey =
                  (r.entry?.date || '') + '|' + (r.entry?.time_in || '') + '|' + idx;
                return (
                  <EntryCard
                    key={entryKey}
                    index={idx}
                    recon={r}
                    choice={perEntryChoices[idx]}
                    onChoice={handleChoice}
                  />
                );
              })}
            </div>

            <DialogFooter className="border-t pt-4 mt-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={submitting}
                data-testid="evv-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || (summary && summary.blocked)}
                className="bg-indigo-900 hover:bg-indigo-800"
                data-testid="evv-confirm-submit-btn"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : summary?.blocked ? (
                  'Supervisor approval required'
                ) : (
                  `Create ${reconciliations.filter((r) => r.recommendation === 'no_evv_record').length} new · Link ${
                    reconciliations.length -
                    reconciliations.filter((r) => r.recommendation === 'no_evv_record').length -
                    reconciliations.filter((r) => r.recommendation === 'invalid_entry').length
                  } existing`
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SummaryBar = ({ summary }) => {
  if (!summary) return null;
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-5 gap-2 py-3 px-1 border-b border-slate-200 bg-slate-50/50 -mx-6 px-6"
      data-testid="evv-summary-bar"
    >
      <Stat
        label="Total"
        value={summary.total_entries}
        color="slate"
        icon={<Info size={14} />}
      />
      <Stat
        label="Exact"
        value={summary.exact_matches}
        color="emerald"
        icon={<CheckCircle size={14} />}
      />
      <Stat
        label="Minor drift"
        value={summary.minor_drifts}
        color="amber"
        icon={<Clock size={14} />}
      />
      <Stat
        label="Major drift"
        value={summary.major_drifts}
        color="orange"
        icon={<Warning size={14} />}
      />
      <Stat
        label="No EVV"
        value={summary.no_evv_records}
        color="blue"
        icon={<Info size={14} />}
      />
    </div>
  );
};

const Stat = ({ label, value, color, icon }) => {
  const bg = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    orange: 'bg-orange-50 text-orange-700',
    blue: 'bg-blue-50 text-blue-700',
  }[color];
  return (
    <div className={`rounded-md px-2 py-1.5 ${bg}`}>
      <div className="flex items-center gap-1 text-[11px] font-medium opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-lg font-bold leading-tight">{value}</div>
    </div>
  );
};

const RECOMMENDATION_STYLES = {
  exact_match: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle size={14} className="text-emerald-600" weight="fill" />,
    label: 'Exact match',
  },
  minor_drift: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <Clock size={14} className="text-amber-600" />,
    label: 'Minor drift',
  },
  major_drift: {
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    icon: <Warning size={14} className="text-orange-600" />,
    label: 'Major drift — review',
  },
  no_evv_record: {
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: <Info size={14} className="text-blue-600" />,
    label: 'Will create new EVV',
  },
  supervisor_required: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    icon: <XCircle size={14} className="text-red-600" weight="fill" />,
    label: 'Supervisor approval required',
  },
  invalid_entry: {
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: <XCircle size={14} className="text-slate-500" />,
    label: 'Invalid entry',
  },
};

const EntryCard = ({ index, recon, choice, onChoice }) => {
  const style = RECOMMENDATION_STYLES[recon.recommendation] || RECOMMENDATION_STYLES.invalid_entry;
  const entry = recon.entry;
  const matchedVisit = recon.matched_visit;
  const showChoices = recon.recommendation === 'major_drift' && matchedVisit;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3"
      data-testid={`evv-entry-${index}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={style.badge}>
            <span className="flex items-center gap-1">
              {style.icon}
              {style.label}
            </span>
          </Badge>
          <span className="text-xs text-slate-500 font-medium">
            Trust: {recon.trust_score}%
          </span>
        </div>
        <div className="text-xs text-slate-500 font-medium">
          {entry?.date || '—'} ({entry?.day_of_week || ''})
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded bg-slate-50 p-2">
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">
            From Timesheet
          </div>
          <div className="font-mono">
            {entry?.time_in || '—'} → {entry?.time_out || '—'}
          </div>
          {entry?.total_hours != null && (
            <div className="text-xs text-slate-500 mt-0.5">
              {entry.total_hours}h
            </div>
          )}
        </div>
        <div className="rounded bg-slate-50 p-2">
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">
            From EVV
          </div>
          {matchedVisit ? (
            <>
              <div className="font-mono">
                {matchedVisit.time_in || '—'} → {matchedVisit.time_out || '—'}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {matchedVisit.validation_status || 'Pending'}
                {matchedVisit.verification_method
                  ? ` · ${matchedVisit.verification_method}`
                  : ''}
              </div>
            </>
          ) : (
            <div className="text-slate-400 text-sm">No matching visit yet</div>
          )}
        </div>
      </div>

      {(recon.drift_in_minutes !== null || recon.drift_out_minutes !== null) && (
        <div className="mt-2 text-xs text-slate-600">
          Drift: <strong>In</strong> {recon.drift_in_minutes ?? '—'}min ·{' '}
          <strong>Out</strong> {recon.drift_out_minutes ?? '—'}min
        </div>
      )}

      <p className="mt-2 text-xs text-slate-500">{recon.message}</p>

      {showChoices && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={choice === 'evv' ? 'default' : 'outline'}
            onClick={() => onChoice(index, 'evv')}
            data-testid={`evv-entry-${index}-use-evv`}
          >
            Use EVV times
          </Button>
          <Button
            type="button"
            size="sm"
            variant={choice === 'timesheet' ? 'default' : 'outline'}
            onClick={() => onChoice(index, 'timesheet')}
            data-testid={`evv-entry-${index}-use-timesheet`}
          >
            Override with timesheet
          </Button>
          <Button
            type="button"
            size="sm"
            variant={choice === 'create' ? 'default' : 'outline'}
            onClick={() => onChoice(index, 'create')}
            data-testid={`evv-entry-${index}-create`}
          >
            Create separate visit
          </Button>
        </div>
      )}
    </div>
  );
};
